import { memo } from 'react';
import { IconButton, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import Tooltip from '@mui/material/Tooltip';
import BuildIcon from '@mui/icons-material/Build';
import LaunchIcon from '@mui/icons-material/Launch';
import CellLink from './CellLink';
import formatDate, { formatUtcDate } from '../../../../utils/formatDate';
import UpdateStatusButton from '../UpdateStatusButton';
import { getSalesforceBookingLink, getSalesforceContactLink } from '../../../../utils/helpers';
import { COLORS } from '../../../../utils/Application_Constants';
import { ProcessedTimesheetContextProvider } from '../../../Contexts/ProcessedTimesheetContext';

const styles = () => ({
    needReview: {
        display: 'flex',
        flexDirection: 'column',

        '& span': {
            maxWidth: '100%',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            textWrap: 'nowrap',
        },
    },
});

function BodyCell({ classes, timesheet, column }) {
    if (column.key === 'status') {
        return (
            <ProcessedTimesheetContextProvider>
                <UpdateStatusButton
                    id={timesheet.id}
                    variant="lt-admin-pill-outlined"
                    status={timesheet.status}
                    needsReviewStatus={timesheet.needsReviewStatus}
                    clinicianName={timesheet.clinicianName}
                    lastModified={timesheet.lastModified}
                    isCorrection={timesheet.isCorrection}
                />
            </ProcessedTimesheetContextProvider>
        );
    }

    let content;
    let showOverflow = false;
    /**
     * Renders the body cell based on the column key
     */
    switch (column.key) {
        case 'isCorrection':
            if (timesheet.isCorrection) {
                content = (
                    <Tooltip title="This timesheet has corrections">
                        <BuildIcon
                            sx={{
                                color: COLORS.LT_POMEGRANATE,
                                verticalAlign: 'middle',
                                width: 17,
                                height: 17,
                            }}
                        />
                    </Tooltip>
                );
            } else {
                content = '';
            }
            break;
        case 'processedTimesheet':
            content = (
                <IconButton
                    href={`#/processed-timesheets/${timesheet.id}`}
                    target="_blank"
                    rel="noreferrer"
                    color="primary"
                    size="small"
                    aria-label="View timesheet"
                >
                    <LaunchIcon />
                </IconButton>
            );
            break;
        case 'recruiter':
            if (timesheet.recruiterSalesforceId && timesheet.recruiterName) {
                content = (
                    <CellLink
                        href={`https://example.com/${timesheet.recruiterSalesforceId}`}
                        label={timesheet.recruiterName}
                    />
                );
            } else {
                content = timesheet.recruiterName;
            }
            break;
        case 'salesperson':
            if (timesheet.salespersonSalesforceId && timesheet.salespersonName) {
                content = (
                    <CellLink
                        href={`https://example.com/${timesheet.salespersonSalesforceId}`}
                        label={timesheet.salespersonName}
                    />
                );
            } else {
                content = timesheet.salespersonName;
            }
            break;
        case 'clinician':
            if (timesheet.clinicianSalesforceId && timesheet.clinicianName) {
                content = (
                    <CellLink
                        href={getSalesforceContactLink(timesheet.clinicianSalesforceId)}
                        label={timesheet.clinicianName}
                    />
                );
            } else {
                content = timesheet.clinicianName;
            }
            break;
        case 'booking':
            if (timesheet.bookingSalesforceId && timesheet.bookingId) {
                content = (
                    <CellLink
                        href={getSalesforceBookingLink(timesheet.bookingSalesforceId)}
                        label={`BKG-${timesheet.bookingId}`}
                    />
                );
            } else {
                content = `BKG-${timesheet.bookingId || 'Unknown'}`;
            }
            break;
        case 'needsReviewReason': {
            let needsReviewReasons = [];
            // TODO: Remove this once the API is updated to return an array of strings
            if (typeof timesheet.needsReviewReason === 'string') {
                needsReviewReasons = timesheet.needsReviewReason.split(';');
            } else if (Array.isArray(timesheet.needsReviewReason)) {
                needsReviewReasons = timesheet.needsReviewReason;
            }
            content = (
                <span className={classes.needReview}>
                    {needsReviewReasons?.map((reason, index) => (
                        <span
                            key={`ts:${timesheet.id}|reason:${encodeURIComponent(reason)}`}
                            title={reason}
                        >
                            {reason}
                            {needsReviewReasons?.length > 1 &&
                                index !== needsReviewReasons.length - 1 &&
                                ';'}
                        </span>
                    ))}
                </span>
            );
            showOverflow = true;
            break;
        }
        case 'teamCoordinatorComments':
            content = <span>{timesheet.teamCoordinatorComments}</span>;
            showOverflow = true;
            break;
        case 'clinicianComments':
            content = timesheet.providerComments;
            showOverflow = true;
            break;
        case 'accountName':
            content = timesheet.accountName;
            break;
        case 'weekEndingDate':
            content = formatUtcDate(timesheet.weekEnding);
            break;
        case 'specialProcessing':
            content = timesheet.specialProcessing;
            break;
        case 'primaryWorkLocation':
            content = timesheet.primaryWorkLocation;
            break;
        case 'numberOfWorkLocations':
            content = timesheet.numberOfWorkLocations;
            break;
        case 'accountCoordinator':
            content = timesheet.accountCoordinator;
            break;
        case 'ab5':
            content = timesheet.aB5 ? 'W2' : '';
            break;
        case 'originalPay':
            content = timesheet.originalPayrollDate
                ? formatUtcDate(timesheet.originalPayrollDate)
                : '';
            break;
        case 'approvalDate':
            content = timesheet.approvalDate ? formatDate(timesheet.approvalDate) : '';
            break;
        case 'monthPosted':
            content = formatUtcDate(timesheet.monthPosted);
            break;
        default:
            content = '';
            break;
    }
    return (
        <Typography
            variant="body2"
            className={showOverflow ? 'show-overflow' : ''}
            data-testid="body-cell-content"
        >
            {content}
        </Typography>
    );
}

const StyledBodyCell = withStyles(styles)(BodyCell);
export default memo(StyledBodyCell);
